import { type ReactElement, useCallback, useEffect, useState } from 'react'
import { useFetcher, useLoaderData } from 'react-router'
import { toast } from 'sonner'

import {
	SubmissionsDataTable,
	type SubmissionRow,
} from '#app/components/mocra/submissions/submissions-table.tsx'
import { Page } from '#app/components/page.tsx'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '#app/components/ui/alert-dialog.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from '#app/components/ui/sheet.tsx'
import { BIN_ID_STORAGE_KEY } from '#app/domains/mocra/sync/jsonbin.client.ts'
import { type Route } from '#app/routes/mocra+/+types/submissions.ts'
import { ProtectedPage } from '#app/routes/mocra+/_form+/components/protected-page.tsx'
import {
	clearAllSections,
	saveSection,
} from '#app/routes/mocra+/_form+/intake-storage.ts'
import {
	deleteBin,
	getBinLatest,
	listCollectionBins,
	type JsonBinCollectionItem,
} from '#app/services/jsonbin.server.ts'

// Use ProtectedPage with its own localStorage key + password
const SUBMISSIONS_AUTH_STORAGE_KEY = 'mocra-submissions-auth'
const SUBMISSIONS_AUTH_TOKEN_BASE64 = 'bW9jcmEtdmlldw==' // btoa('mocra-view')

type NormalizedBin = {
	id: string
	name: string | null
	createdAt: string | null
	updatedAt: string | null
}

export async function loader({}: Route.LoaderArgs) {
	const collectionId =
		process.env.JSONBIN_COLLECTION_ID || '68fc2d74d0ea881f40b9b611'
	let bins: JsonBinCollectionItem[] = []
	if (collectionId) {
		try {
			bins = await listCollectionBins(collectionId)
		} catch {
			bins = []
		}
	}
	// Normalize only (client-side table handles sorting/filtering)
	const normalized: NormalizedBin[] = bins
		.map((b) => {
			// Type guard to check if value is a record-like object
			const isRecord = (value: unknown): value is Record<string, unknown> => {
				return typeof value === 'object' && value !== null
			}

			// Helper to safely get nested property
			const getNestedString = (obj: unknown, key: string): string | null => {
				if (!isRecord(obj)) return null
				const value = obj[key]
				return typeof value === 'string' ? value : null
			}

			const metadata = isRecord(b.metadata) ? b.metadata : null
			const snippetMeta = isRecord(b.snippetMeta) ? b.snippetMeta : null

			// Try to extract id from various possible locations
			const id: string = String(
				b.id ??
					getNestedString(metadata, 'id') ??
					b.record ??
					getNestedString(metadata, 'record') ??
					'',
			)

			const name: string | null =
				b.name ??
				getNestedString(metadata, 'name') ??
				getNestedString(snippetMeta, 'name') ??
				null

			const createdAt: string | null =
				b.createdAt ?? getNestedString(metadata, 'createdAt') ?? null

			const updatedAt: string | null =
				b.updatedAt ??
				getNestedString(metadata, 'updatedAt') ??
				getNestedString(b, 'modifiedAt') ??
				getNestedString(metadata, 'modifiedAt') ??
				null

			return { id, name, createdAt, updatedAt }
		})
		.filter((b) => Boolean(b.id))
	return {
		collectionConfigured: Boolean(collectionId),
		collectionId,
		bins: normalized,
	}
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const intent = String(formData.get('intent') || '')

	if (intent === 'FETCH') {
		const binId = String(formData.get('binId') || '')
		if (!binId) return new Response('Missing binId', { status: 400 })
		try {
			const data = await getBinLatest(binId)
			return new Response(JSON.stringify({ ok: true, data }), {
				headers: { 'content-type': 'application/json' },
			})
		} catch (e) {
			const message = e instanceof Error ? e.message : 'Unknown error'
			return new Response(JSON.stringify({ ok: false, message }), {
				status: 500,
				headers: { 'content-type': 'application/json' },
			})
		}
	} else if (intent === 'DELETE') {
		const binId = String(formData.get('binId') || '')
		if (!binId) return new Response('Missing binId', { status: 400 })
		try {
			await deleteBin(binId)
			return new Response(JSON.stringify({ ok: true }), {
				headers: { 'content-type': 'application/json' },
			})
		} catch (e) {
			const message = e instanceof Error ? e.message : 'Unknown error'
			return new Response(JSON.stringify({ ok: false, message }), {
				status: 500,
				headers: { 'content-type': 'application/json' },
			})
		}
	}

	return new Response('Bad Request', { status: 400 })
}

export default function SubmissionsPage() {
	const { bins } = useLoaderData<typeof loader>()
	const fetcher = useFetcher<{
		ok: boolean
		data?: { id: string; record: unknown } | undefined
		message?: string
	}>()
	const loadFetcher = useFetcher<{
		ok: boolean
		data?: { id: string; record: unknown } | undefined
		message?: string
	}>()
	const [selectedId, setSelectedId] = useState<string | null>(null)
	const [sheetOpen, setSheetOpen] = useState(false)

	const handleLoad = useCallback(
		(id: string) => {
			const fd = new FormData()
			fd.set('intent', 'FETCH')
			fd.set('binId', id)
			void loadFetcher.submit(fd, {
				method: 'post',
				action: '/mocra/submissions',
			})
		},
		[loadFetcher],
	)

	useEffect(() => {
		const data = loadFetcher.data
		if (!data) return
		if (!data.ok || !data.data) {
			toast.error(data.message || 'Unable to load submission')
			return
		}
		try {
			const record = data.data.record
			let formData: Record<string, unknown> | null = null
			if (record && typeof record === 'object') {
				const rec = record as Record<string, unknown>
				if (
					rec.formData &&
					typeof rec.formData === 'object' &&
					rec.formData !== null
				) {
					formData = rec.formData as Record<string, unknown>
				} else {
					formData = rec
				}
			}
			if (!formData) throw new Error('Submission payload missing form data')

			clearAllSections()
			for (const [key, value] of Object.entries(formData)) {
				try {
					saveSection(key, value)
				} catch {}
			}
			try {
				if (typeof window !== 'undefined') {
					window.localStorage.setItem(BIN_ID_STORAGE_KEY, data.data.id)
				}
			} catch {}
			toast.success('Draft loaded. Open the MoCRA form to review and edit.')
		} catch (e) {
			const msg =
				e instanceof Error ? e.message : 'Unable to process submission data'
			toast.error(msg)
		}
	}, [loadFetcher.data])

	return (
		<Page title="MoCRA Submissions" className="space-y-6">
			<ProtectedPage
				storageKey={SUBMISSIONS_AUTH_STORAGE_KEY}
				authTokenBase64={SUBMISSIONS_AUTH_TOKEN_BASE64}
			>
				{/* Toast handles success/error notifications */}
				{/*<div className="flex items-center gap-2 text-sm">
					<span>Collection:</span>
					<code className="rounded-md bg-neutral-100 p-1 text-xs">
						{collectionId ? ` ${collectionId}` : ''}
					</code>
				</div>*/}

				{bins && bins.length > 0 ? (
					<SubmissionsDataTable
						data={bins as SubmissionRow[]}
						onRowClick={(id) => {
							setSelectedId(id)
							setSheetOpen(true)
							void fetcher.submit(
								{ intent: 'FETCH', binId: id },
								{ method: 'post' },
							)
						}}
						onLoad={handleLoad}
						loading={loadFetcher.state === 'submitting'}
					/>
				) : null}

				<JsonSheet
					open={sheetOpen}
					onOpenChange={setSheetOpen}
					binId={selectedId}
					fetcherOk={Boolean(fetcher.data?.ok)}
					fetcherRecord={fetcher.data?.data?.record}
					errorMessage={
						fetcher.data && !fetcher.data.ok ? fetcher.data.message : undefined
					}
					onDelete={async (id) => {
						const fd = new FormData()
						fd.set('intent', 'DELETE')
						fd.set('binId', id)
						const resp = await fetch('/mocra/submissions', {
							method: 'POST',
							body: fd,
						})
						if (resp.ok) {
							window.location.reload()
						}
					}}
				/>
			</ProtectedPage>
		</Page>
	)
}

function JsonSheet({
	open,
	onOpenChange,
	binId,
	fetcherOk,
	fetcherRecord,
	errorMessage,
	onDelete,
}: {
	open: boolean
	onOpenChange: (next: boolean) => void
	binId: string | null
	fetcherOk: boolean
	fetcherRecord: unknown
	errorMessage?: string
	onDelete: (id: string) => void | Promise<void>
}) {
	const [query, setQuery] = useState('')
	const [currentMatch, setCurrentMatch] = useState(0)
	const jsonText = (() => {
		if (!fetcherOk || !fetcherRecord) return ''
		try {
			return JSON.stringify(fetcherRecord, null, 2)
		} catch {
			return String(fetcherRecord)
		}
	})()

	const { parts, count } = (() => {
		if (!query.trim() || !jsonText) return { parts: [jsonText], count: 0 }
		try {
			const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
			const re = new RegExp(safe, 'gi')
			const segments: Array<string | ReactElement> = []
			let lastIndex = 0
			let idx = 0
			let match: RegExpExecArray | null
			while ((match = re.exec(jsonText)) !== null) {
				const start = match.index
				const end = start + match[0].length
				if (lastIndex < start) segments.push(jsonText.slice(lastIndex, start))
				const isCurrent = idx === currentMatch
				segments.push(
					<mark
						id={`json-match-${idx}`}
						key={`match-${idx}-${start}`}
						className={isCurrent ? 'bg-yellow-300/80' : 'bg-yellow-200/60'}
					>
						{jsonText.slice(start, end)}
					</mark>,
				)
				lastIndex = end
				idx += 1
			}
			if (lastIndex < jsonText.length) segments.push(jsonText.slice(lastIndex))
			return { parts: segments, count: idx }
		} catch {
			return { parts: [jsonText], count: 0 }
		}
	})()

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent side="right" className="sm:max-w-[35vw]">
				<SheetHeader>
					<SheetTitle>Submission JSON</SheetTitle>
					<div className="text-muted-foreground text-xs">{binId ?? ''}</div>
				</SheetHeader>
				<div className="mt-3 flex h-[calc(100%-96px)] flex-col gap-2">
					<input
						className="border-input bg-background text-foreground h-9 w-full rounded-md border px-3 text-sm outline-none"
						placeholder="Search in JSON"
						value={query}
						onChange={(e) => {
							setQuery(e.target.value)
							setCurrentMatch(0)
						}}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault()
								const next = (currentMatch + 1) % Math.max(1, count)
								setCurrentMatch(next)
								const el = document.getElementById(`json-match-${next}`)
								el?.scrollIntoView({ block: 'center' })
							}
						}}
					/>
					<div className="border-border bg-muted/40 h-full overflow-auto rounded border p-3 text-xs">
						{errorMessage ? (
							<p className="text-destructive">{errorMessage}</p>
						) : jsonText ? (
							<pre className="whitespace-pre-wrap">{parts}</pre>
						) : (
							<p className="text-muted-foreground">Loading…</p>
						)}
					</div>
					<div className="flex items-center justify-end gap-2">
						{binId ? (
							<AlertDialog>
								<AlertDialogTrigger asChild>
									<Button type="button" variant="destructive">
										Delete
									</Button>
								</AlertDialogTrigger>
								<AlertDialogContent>
									<AlertDialogHeader>
										<AlertDialogTitle>Delete this submission?</AlertDialogTitle>
										<AlertDialogDescription>
											This action permanently deletes the bin (ID: {binId}) from
											JSONBin. This cannot be undone.
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel>Cancel</AlertDialogCancel>
										<AlertDialogAction asChild>
											<Button
												type="button"
												variant="destructive"
												onClick={() => onDelete(binId)}
											>
												Delete
											</Button>
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						) : null}
					</div>
				</div>
			</SheetContent>
		</Sheet>
	)
}

//
